import type { exportProducts } from 'services';
import type {
  BaseAttribute,
  BaseTreeOption,
  CityTree,
  FileOwnerType,
  Product,
  Province,
  PurchaseProduct,
  ResponseErrors,
  SelectOption,
  TableSource,
  TreeSelectSource,
} from 'types';
import { CONFIG, fixedParseFloat, PATHNAME, preciseRound } from 'utils';

const {
  APP: { BASE_URL: APP_BASE_URL },
  API: { BASE_URL: API_BASE_URL },
} = CONFIG;
const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR, LOCALE: DEFAULT_LOCALE },
  QUERY_PARAM: {
    DEFAULT: {
      QUESTION_MARK: DEFAULT_QUESTION_MARK,
      AMPERSAND: DEFAULT_AMPERSAND,
      ASSIGNMENT: DEFAULT_ASSIGNMENT,
    },
    CULTURE: { BASE_URL: CULTURE_BASE_URL },
  },
} = PATHNAME;

const generateRequestUrl = <P extends object = object>(
  url: string,
  params?: P,
) => {
  let queryParams = '';

  if (params) {
    queryParams = Object.entries(params)
      .flatMap(([key, value]) => {
        if (value === undefined || value === null) return [];

        if (Array.isArray(value)) {
          return value.map(
            v => `${key}${DEFAULT_ASSIGNMENT}${encodeURIComponent(v)}`,
          );
        }

        return `${key}${DEFAULT_ASSIGNMENT}${encodeURIComponent(value)}`;
      })
      .join(DEFAULT_AMPERSAND);
  }

  return `${API_BASE_URL}${DEFAULT_SLUG_SEPARATOR}${url}${DEFAULT_SLUG_SEPARATOR}${DEFAULT_QUESTION_MARK}${CULTURE_BASE_URL}${DEFAULT_ASSIGNMENT}${DEFAULT_LOCALE}${queryParams ? `${DEFAULT_AMPERSAND}${queryParams}` : queryParams}`;
};

const generateDownloadFile = (
  ownerType: FileOwnerType,
  id: string,
  width = 200,
  height = 200,
  quality = 80,
) =>
  generateRequestUrl(
    `file${DEFAULT_SLUG_SEPARATOR}downloadFile${DEFAULT_SLUG_SEPARATOR}${ownerType}`,
    {
      id,
      width,
      height,
      quality,
    },
  );

const generateDownloadThumbnail = (
  ownerType: FileOwnerType,
  id: string,
  width = 200,
  height = 200,
) =>
  generateRequestUrl(
    `file${DEFAULT_SLUG_SEPARATOR}downloadThumbnail${DEFAULT_SLUG_SEPARATOR}${ownerType}`,
    {
      id,
      width,
      height,
    },
  );

const generateDownloadInvoiceFile = (purchaseId: string) =>
  generateRequestUrl(`file${DEFAULT_SLUG_SEPARATOR}downloadInvoiceFile`, {
    purchaseId,
  });

const generateExternalPathname = (pathname: string, slug: string) =>
  `${APP_BASE_URL}${pathname}${slug}${DEFAULT_SLUG_SEPARATOR}`;

const generateResponseError = ({ errors }: ResponseErrors) => {
  let message = '';

  errors.forEach(error => {
    if ('field' in error) {
      message = error.messages.join('، ');
    }
    if ('code' in error) {
      message = error.message;
    }
  });

  return message;
};

const generateBase64 = <T extends string>(blob: Blob) =>
  new Promise<T>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result as T);
    reader.onerror = error => reject(error);
  });

const generateVariableSubProductTitle = ({
  name = '',
  type,
  selectedValues = [],
}: Partial<BaseAttribute>) => ({
  name,
  value:
    type === 'CheckBox'
      ? selectedValues.join('، ')
      : selectedValues.at(0) ?? '-',
});

const generateSubProductTitle = ({
  name,
  productType,
  attributes = [],
}: Partial<Product & Record<'attributes', Partial<BaseAttribute>[]>>) =>
  productType === 'Normal'
    ? name
    : productType === 'Variable'
      ? attributes.map(attribute => generateVariableSubProductTitle(attribute))
      : undefined;

const generatePurchaseProductInvoice = <T extends PurchaseProduct>(
  products: T | T[],
  amount: keyof T | number,
) =>
  (Array.isArray(products) ? products : [products]).reduce(
    (previousValue, currentValue) => {
      const {
        price: totalPrice,
        originalPrice: totalOriginalPrice,
        size,
        packagingInfo,
      } = currentValue;
      const { amount: packagingInfoAmount } = packagingInfo;
      const unitPrice = totalPrice / size;
      const unitOriginalPrice = totalOriginalPrice / size;

      const productAmount =
        typeof amount !== 'number' ? currentValue[amount] : amount;

      const calculateInvoiceValue = (value: number) => {
        const roundedValue = preciseRound(
          Number(productAmount),
          packagingInfoAmount,
        );
        const result = fixedParseFloat(roundedValue * value);

        return Math.abs(result);
      };

      return {
        originalPrice:
          previousValue.originalPrice +
          calculateInvoiceValue(unitOriginalPrice),
        price: previousValue.price + calculateInvoiceValue(unitPrice),
      };
    },
    { originalPrice: 0, price: 0 },
  );

const generateUniqueElements = <T, K extends keyof T = any>(
  elements: T[],
  key?: K,
): (T[K] | T)[] => {
  const uniqueElements = new Set<T[K] | T>();

  elements.forEach(element => {
    if (typeof element === 'object' && element !== null && key) {
      if (key in element) {
        uniqueElements.add(element[key]);
      }
    } else {
      uniqueElements.add(element);
    }
  });

  return Array.from(uniqueElements);
};

const generateSelectOptions = <
  T extends BaseTreeOption<T>,
  K extends keyof T,
  L extends keyof T,
  V extends keyof T,
>(
  elements: T[],
  key: K,
  label: L,
  value: V,
): SelectOption[] =>
  elements.map(element => {
    const { children = [], ...otherElements } = element;

    return {
      ...otherElements,
      key: element[key],
      label: element[label],
      value: element[value],
      children:
        children.length > 0
          ? generateSelectOptions(children, key, label, value)
          : undefined,
    };
  });

const generateTreeSelectData = <
  T extends BaseTreeOption<T>,
  K extends keyof T,
  L extends keyof T,
  V extends keyof T,
>(
  elements: T[],
  key: K,
  label: L,
  value: V,
): TreeSelectSource[] =>
  elements.map(element => {
    const { children = [], ...otherElements } = element;

    return {
      ...otherElements,
      key: element[key],
      label: element[label],
      value: element[value],
      children:
        children.length > 0
          ? generateTreeSelectData(children, key, label, value)
          : undefined,
    };
  });

const generateTableDataSource = <
  T extends BaseTreeOption<T>,
  K extends keyof T,
>(
  elements: T[],
  key: K,
): TableSource<T>[] =>
  elements.map(element => {
    const { children = [], ...otherElements } = element;

    return {
      ...otherElements,
      key: element[key],
      children:
        children.length > 0
          ? generateTableDataSource(children, key)
          : undefined,
    };
  });

const findElementByKey = <T, K extends keyof T>(
  elements: T[],
  key: K,
  value: T[K],
) => elements.find(element => element[key] === value);

const findTreeElementByKey = <T extends BaseTreeOption<T>, K extends keyof T>(
  elements: T[],
  key: K,
  value: T[K],
): T | undefined => {
  for (const element of elements) {
    const { children } = element;
    if (element[key] === value) return element;

    if (children) {
      const foundedElement = findTreeElementByKey(children, key, value);
      if (foundedElement) return foundedElement;
    }
  }
  return undefined;
};

const mergeDuplicateElements = <
  T extends Record<K, PropertyKey>,
  K extends keyof T,
  M extends keyof T,
>(
  elements: readonly T[],
  key: K,
  modifier: M,
  callback: (existing: T[M], incoming: T[M]) => T[M],
): T[] => {
  const elementMap = new Map<PropertyKey, T>();

  for (const element of elements) {
    const elementKey = element[key];
    const existingElement = elementMap.get(elementKey);

    elementMap.set(
      elementKey,
      existingElement
        ? {
            ...existingElement,
            [modifier]: callback(existingElement[modifier], element[modifier]),
          }
        : { ...element },
    );
  }

  return Array.from(elementMap.values());
};

const mergeArraysByKey = <T extends Record<K, PropertyKey>, K extends keyof T>(
  primaryItems: readonly T[],
  secondaryItems: readonly T[],
  key: K,
  callback: (primary: T, secondary: T) => T,
): T[] => {
  const itemMap = new Map<PropertyKey, T>();

  primaryItems.forEach(item => itemMap.set(item[key], { ...item }));
  secondaryItems.forEach(item => {
    const itemKey = item[key];
    const existingItem = itemMap.get(itemKey);

    itemMap.set(
      itemKey,
      existingItem ? callback(existingItem, item) : { ...item },
    );
  });

  return Array.from(itemMap.values());
};

const mapProvinceToTree = (province: Province): CityTree => {
  return {
    id: province.id,
    name: province.name,
    children: province.cities.map(c => {
      return {
        id: c.id,
        name: c.name,
      } as CityTree;
    }),
  };
};

export {
  findElementByKey,
  findTreeElementByKey,
  generateBase64,
  generateDownloadFile,
  generateDownloadInvoiceFile,
  generateDownloadThumbnail,
  generateExternalPathname,
  generatePurchaseProductInvoice,
  generateRequestUrl,
  generateResponseError,
  generateSelectOptions,
  generateSubProductTitle,
  generateTableDataSource,
  generateTreeSelectData,
  generateUniqueElements,
  generateVariableSubProductTitle,
  mapProvinceToTree,
  mergeArraysByKey,
  mergeDuplicateElements,
};
